/**
 * Eingangsrechnungen (Baustein „Eingangsrechnungen Versand“, Rulings R93–R97).
 *
 * Erster Lieferant: DHL Paket (Geschäftskundenrechnung als PDF). Weg heute: Download im DHL-Geschäftskundenportal,
 * Upload in StoreOS (`quelle = upload`). Später kommt dieselbe Rechnung per E-Mail bzw. als ESN-Datei per SFTP
 * (`quelle = email | sftp`); nur die Quelle ändert sich, Parser, Prüfung, Festschreiben und Übergabe bleiben.
 *
 * Node-frei: Plausibilitätsprüfung, Kategorien, Abgleich und Kennzahlen laufen in API und SPA identisch.
 * Die Textextraktion aus dem PDF (unpdf) und die Parser je Lieferant (`LieferantenrechnungParser`) liegen in der API.
 *
 * Datenschutz/Sicherheit: Ein Parser liest nur die hier typisierten Felder. Bankverbindung, IBAN, Gläubiger-ID und
 * Mandatsreferenz der Rechnung werden weder gelesen noch gespeichert noch geloggt; das Original-PDF wird
 * unverändert aufbewahrt (GoBD), aber nie als Text in DB oder Log übernommen.
 */
import { rundeCents, type Cents } from './geld.js';

export const EINGANG_LIEFERANTEN = ['dhl_paket'] as const;
export type EingangLieferant = (typeof EINGANG_LIEFERANTEN)[number];
export const EINGANG_LIEFERANT_TEXT: Record<EingangLieferant, string> = { dhl_paket: 'DHL Paket' };

/** Woher die Rechnung kam. Heute nur `upload`; `email`/`sftp` sind vorgesehen (R93). */
export const EINGANG_QUELLEN = ['upload', 'email', 'sftp'] as const;
export type EingangQuelle = (typeof EINGANG_QUELLEN)[number];

export const EINGANG_STATUS = ['hochgeladen', 'klaerfall', 'geprueft', 'festgeschrieben', 'an_buchhaltung', 'verworfen'] as const;
export type EingangStatus = (typeof EINGANG_STATUS)[number];
export const EINGANG_STATUS_TEXT: Record<EingangStatus, string> = {
  hochgeladen: 'hochgeladen', klaerfall: 'Klärfall', geprueft: 'geprüft', festgeschrieben: 'festgeschrieben',
  an_buchhaltung: 'an Buchhaltung', verworfen: 'verworfen',
};

/**
 * Statusautomat (R94). Daten werden nie geändert; nur der Status läuft vorwärts. `klaerfall → geprueft` nur mit
 * Grund (Abweichung bleibt dokumentiert). `verworfen` nur vor dem Festschreiben. Die DB prüft dieselbe Tabelle
 * (Migration 400, Trigger `eingangsrechnungen_schutz`).
 */
export const EINGANG_UEBERGAENGE: Record<EingangStatus, readonly EingangStatus[]> = {
  hochgeladen: ['geprueft', 'verworfen'],
  klaerfall: ['geprueft', 'verworfen'],
  geprueft: ['festgeschrieben', 'verworfen'],
  festgeschrieben: ['an_buchhaltung'],
  an_buchhaltung: [],
  verworfen: [],
};
export function darfEingangWechseln(von: EingangStatus, nach: EingangStatus): boolean {
  return EINGANG_UEBERGAENGE[von].includes(nach);
}

export const EINGANG_KATEGORIEN = ['porto', 'zuschlag_maut', 'zuschlag_energie', 'pauschale', 'service', 'sonstiges'] as const;
export type EingangKategorie = (typeof EINGANG_KATEGORIEN)[number];
export const EINGANG_KATEGORIE_TEXT: Record<EingangKategorie, string> = {
  porto: 'Porto', zuschlag_maut: 'Maut-/CO2-Zuschlag', zuschlag_energie: 'Energiezuschlag', pauschale: 'Pauschale', service: 'Service', sonstiges: 'Sonstiges',
};

/** Abschnitt der Rechnung: laufender Einlieferungszeitraum, Nachberechnung aus Vorperioden, Pauschale, Unbekanntes. */
export const EINGANG_ABSCHNITTE = ['aktuell', 'vorperiode', 'pauschale', 'sonstiges'] as const;
export type EingangAbschnitt = (typeof EINGANG_ABSCHNITTE)[number];

// ── Erkannte Rechnung (Ergebnis eines Parsers) ───────────────────────────────

export interface ErkanntePosition {
  abschnitt: EingangAbschnitt;
  /** Einlieferungsdatum YYYY-MM-DD (bei Pauschalen null). */
  datum: string | null;
  /** Blatt- bzw. ICR-Nummer des Einlieferungsauftrags. */
  blatt: string | null;
  /** Abrechnungsnummer (Pauschale je Abrechnungsnummer). */
  abrechnungsnummer: string | null;
  produkt: string;
  kategorie: EingangKategorie;
  /** '1 kg', '2 kg', '5 kg', '10 kg', '20 kg', '31,5 kg', 'Kleinpaket' … nur bei Porto. */
  gewichtsklasse: string | null;
  anzahl: number;
  einheit: string | null;
  einzelpreis_cents: Cents;
  gesamt_cents: Cents;
  ust_kz: string | null;
}

export interface ErkannteZusammenfassung {
  produkt: string;
  kategorie: EingangKategorie;
  anzahl: number;
  gesamt_cents: Cents;
  ust_kz: string | null;
}

export interface ErkannteSteuer {
  /** Kennzeichen der Rechnung (DHL: C1 = voller Satz). */
  kz: string;
  /** Satz in Basispunkten (19 % = 1900); INT statt Gleitkomma. */
  satz_bp: number;
  basis_cents: Cents;
  betrag_cents: Cents;
}

export interface ErkannteRechnung {
  lieferant: EingangLieferant;
  lieferant_name: string;
  rechnungsnummer: string;
  /** Rechnungsdatum YYYY-MM-DD. */
  datum: string;
  /** Leistungszeitraum YYYY-MM. */
  leistungszeitraum: string | null;
  kundennummer: string | null;
  abrechnungsnummern: string[];
  einlieferung_von: string | null;
  einlieferung_bis: string | null;
  netto_cents: Cents;
  ust_cents: Cents;
  brutto_cents: Cents;
  /** Steuerzeilen der ersten Seite („Umsatzsteuer (C1) 19 % von …“). */
  steuer: ErkannteSteuer[];
  /** Erläuterung der Kennzeichen (Kz → Satz in bp). */
  steuer_erlaeuterung: Record<string, number>;
  /** Zeilen der ZUSAMMENFASSUNG (ohne Zwischenzeilen „Anzahl Sendungen/Services“, „Summe“). */
  zusammenfassung: ErkannteZusammenfassung[];
  /** „Summe“ der Zusammenfassung (Sendungen + Zuschläge, ohne Pauschale). */
  summe_sendungen_cents: Cents | null;
  /** „Summe (aktueller Zeitraum)“ der Einzelübersicht. */
  summe_einzel_aktuell_cents: Cents | null;
  /** „Summe:“ der Pauschalen-Seite. */
  summe_pauschale_cents: Cents | null;
  /** „Anzahl Sendungen“ der Zusammenfassung bzw. der Einzelübersicht. */
  anzahl_sendungen: number | null;
  anzahl_sendungen_einzel: number | null;
  positionen: ErkanntePosition[];
}

// ── Text eines PDFs (Eingabe der Parser) ─────────────────────────────────────

export interface PdfZelle { x: number; text: string }
export interface PdfZeile { seite: number; y: number; zellen: PdfZelle[]; text: string }
export interface PdfText { seiten: number; zeilen: PdfZeile[] }

/** Adapter je Lieferant (R95). Weitere Lieferanten (Druckerei, Verpackung) implementieren denselben Vertrag. */
export interface LieferantenrechnungParser {
  readonly lieferant: EingangLieferant;
  readonly name: string;
  /** Version der Erkennungsregeln; steht an jeder Rechnung (Nachvollziehbarkeit). */
  readonly version: string;
  erkennt(text: PdfText): boolean;
  /** Wirft `ParserFehler`-artig (Error), wenn Pflichtfelder fehlen; rechnet nichts zurecht. */
  parse(text: PdfText): ErkannteRechnung;
}

// ── Plausibilität (R96) ───────────────────────────────────────────────────────

export interface PruefBefund {
  code: string;
  schwere: 'fehler' | 'hinweis';
  text: string;
  erwartet_cents?: Cents;
  ist_cents?: Cents;
}
export interface Pruefergebnis { ok: boolean; befunde: PruefBefund[] }

const euro = (c: Cents) => `${(c / 100).toFixed(2).replace('.', ',')} €`;
const summe = (xs: ReadonlyArray<{ gesamt_cents: Cents }>) => xs.reduce((s, x) => s + x.gesamt_cents, 0);

/** USt aus Netto und Satz (bp), kaufmännisch gerundet. */
export function ustAusNetto(nettoCents: Cents, satzBp: number): Cents {
  return rundeCents((nettoCents * satzBp) / 10_000);
}

/**
 * Prüft eine erkannte Rechnung gegen sich selbst. Korrigiert nichts: jede Abweichung ist ein Befund.
 * `fehler` → Status `klaerfall`, `hinweis` → nur angezeigt.
 */
export function pruefeRechnung(r: ErkannteRechnung): Pruefergebnis {
  const b: PruefBefund[] = [];
  const fehler = (code: string, text: string, erwartet?: Cents, ist?: Cents) => b.push({ code, schwere: 'fehler', text, erwartet_cents: erwartet, ist_cents: ist });
  const hinweis = (code: string, text: string) => b.push({ code, schwere: 'hinweis', text });

  const pos = summe(r.positionen);
  if (pos !== r.netto_cents) fehler('positionen_netto', `Summe der Positionen ${euro(pos)} ≠ Rechnungsbetrag netto ${euro(r.netto_cents)}`, r.netto_cents, pos);
  if (r.zusammenfassung.length) {
    const zus = summe(r.zusammenfassung);
    if (zus !== r.netto_cents) fehler('zusammenfassung_netto', `Summe der Zusammenfassung ${euro(zus)} ≠ Rechnungsbetrag netto ${euro(r.netto_cents)}`, r.netto_cents, zus);
  } else {
    fehler('zusammenfassung_fehlt', 'Keine Zusammenfassung erkannt');
  }
  const aktuell = r.positionen.filter((p) => p.abschnitt === 'aktuell');
  if (r.summe_einzel_aktuell_cents !== null && summe(aktuell) !== r.summe_einzel_aktuell_cents) {
    fehler('einzel_summe', `Einzelübersicht: Positionen ${euro(summe(aktuell))} ≠ „Summe (aktueller Zeitraum)“ ${euro(r.summe_einzel_aktuell_cents)}`, r.summe_einzel_aktuell_cents, summe(aktuell));
  }
  if (r.summe_sendungen_cents !== null && r.summe_einzel_aktuell_cents !== null && r.summe_sendungen_cents !== r.summe_einzel_aktuell_cents
    && !r.positionen.some((p) => p.abschnitt === 'vorperiode')) {
    fehler('summe_zusammenfassung_einzel', `„Summe“ der Zusammenfassung ${euro(r.summe_sendungen_cents)} ≠ Summe der Einzelübersicht ${euro(r.summe_einzel_aktuell_cents)}`, r.summe_sendungen_cents, r.summe_einzel_aktuell_cents);
  }
  const pauschalen = r.positionen.filter((p) => p.abschnitt === 'pauschale');
  if (r.summe_pauschale_cents !== null && summe(pauschalen) !== r.summe_pauschale_cents) {
    fehler('pauschale_summe', `Pauschalen ${euro(summe(pauschalen))} ≠ „Summe“ der Pauschalen-Seite ${euro(r.summe_pauschale_cents)}`, r.summe_pauschale_cents, summe(pauschalen));
  }

  // Steuer: je Kennzeichen Satz laut Erläuterung, Betrag = Basis × Satz; Summen.
  if (!r.steuer.length) fehler('ust_fehlt', 'Keine Umsatzsteuerzeile erkannt');
  for (const s of r.steuer) {
    const satz = r.steuer_erlaeuterung[s.kz];
    if (satz === undefined) fehler('ust_kz_unbekannt', `Steuerkennzeichen ${s.kz} ist in den Erläuterungen nicht erklärt`);
    else if (satz !== s.satz_bp) fehler('ust_kz_satz', `Kennzeichen ${s.kz}: Satz ${s.satz_bp / 100} % ≠ Erläuterung ${satz / 100} %`);
    const soll = ustAusNetto(s.basis_cents, s.satz_bp);
    if (soll !== s.betrag_cents) fehler('ust_satz', `USt ${s.kz}: ${euro(s.basis_cents)} × ${s.satz_bp / 100} % = ${euro(soll)}, Rechnung ${euro(s.betrag_cents)}`, soll, s.betrag_cents);
  }
  const basis = r.steuer.reduce((x, s) => x + s.basis_cents, 0);
  const ust = r.steuer.reduce((x, s) => x + s.betrag_cents, 0);
  if (r.steuer.length && basis !== r.netto_cents) fehler('ust_basis', `Steuerbasis ${euro(basis)} ≠ netto ${euro(r.netto_cents)}`, r.netto_cents, basis);
  if (r.steuer.length && ust !== r.ust_cents) fehler('ust_summe', `Summe der Steuerzeilen ${euro(ust)} ≠ USt ${euro(r.ust_cents)}`, r.ust_cents, ust);
  if (r.netto_cents + r.ust_cents !== r.brutto_cents) {
    fehler('brutto', `netto ${euro(r.netto_cents)} + USt ${euro(r.ust_cents)} = ${euro(r.netto_cents + r.ust_cents)} ≠ brutto ${euro(r.brutto_cents)}`, r.netto_cents + r.ust_cents, r.brutto_cents);
  }
  for (const kz of new Set(r.positionen.map((p) => p.ust_kz).filter((k): k is string => Boolean(k)))) {
    if (!r.steuer.some((s) => s.kz === kz)) fehler('ust_kz_ohne_steuer', `Positionen mit Kennzeichen ${kz}, aber keine Steuerzeile dazu`);
  }

  // Stückzahlen: Porto-Positionen = „Anzahl Sendungen“.
  const porto = aktuell.filter((p) => p.kategorie === 'porto').reduce((x, p) => x + p.anzahl, 0);
  if (r.anzahl_sendungen !== null) {
    const zusPorto = r.zusammenfassung.filter((z) => z.kategorie === 'porto').reduce((x, z) => x + z.anzahl, 0);
    if (zusPorto !== r.anzahl_sendungen) fehler('anzahl_sendungen', `Zusammenfassung: ${zusPorto} Pakete in den Zeilen, „Anzahl Sendungen“ ${r.anzahl_sendungen}`);
    else if (porto !== r.anzahl_sendungen && !r.positionen.some((p) => p.abschnitt === 'vorperiode')) fehler('anzahl_sendungen', `Einzelübersicht: ${porto} Pakete, Zusammenfassung ${r.anzahl_sendungen}`);
  }
  if (r.anzahl_sendungen_einzel !== null && porto !== r.anzahl_sendungen_einzel) fehler('anzahl_sendungen_einzel', `Einzelübersicht: ${porto} Pakete in den Zeilen, „Anzahl Sendungen“ ${r.anzahl_sendungen_einzel}`);

  // Zeilen: Anzahl × Einzelpreis. DHL rundet den Einzelpreis; eine Abweichung ist ein Hinweis, kein Fehler.
  for (const [i, p] of r.positionen.entries()) {
    if (p.anzahl * p.einzelpreis_cents !== p.gesamt_cents && p.einzelpreis_cents !== 0) {
      hinweis('zeile_rechnung', `Position ${i + 1} (${p.produkt}${p.datum ? `, ${p.datum}` : ''}): ${p.anzahl} × ${euro(p.einzelpreis_cents)} = ${euro(p.anzahl * p.einzelpreis_cents)}, ausgewiesen ${euro(p.gesamt_cents)}`);
    }
  }
  const sonstige = r.positionen.filter((p) => p.kategorie === 'sonstiges' || p.kategorie === 'service');
  if (sonstige.length) hinweis('sonstiges', `${sonstige.length} Position(en) ohne feste Kategorie (Service/Sonstiges): ${[...new Set(sonstige.map((p) => p.produkt))].join(', ')}. Bitte prüfen.`);
  const vor = r.positionen.filter((p) => p.abschnitt === 'vorperiode');
  if (vor.length) hinweis('vorperiode', `${vor.length} Position(en) aus früheren Abrechnungszeiträumen (${euro(summe(vor))}). Abgleich nur gegen die Tage dieser Rechnung.`);
  const gutschriften = r.positionen.filter((p) => p.gesamt_cents < 0);
  if (gutschriften.length) hinweis('gutschrift', `${gutschriften.length} Gutschrift-Position(en) über ${euro(summe(gutschriften))}`);
  const unbekannt = r.positionen.filter((p) => p.abschnitt === 'sonstiges');
  if (unbekannt.length) hinweis('abschnitt_unbekannt', `${unbekannt.length} Position(en) in einem unbekannten Abschnitt der Einzelübersicht`);

  return { ok: !b.some((x) => x.schwere === 'fehler'), befunde: b };
}

// ── Kategorien, Gewichtsklassen ───────────────────────────────────────────────

export function kategorieFuerProdukt(produkt: string): EingangKategorie {
  const p = produkt.toLowerCase();
  if (/maut|co2/.test(p)) return 'zuschlag_maut';
  if (/energiezuschlag/.test(p)) return 'zuschlag_energie';
  if (/pauschale/.test(p)) return 'pauschale';
  if (/^dhl\s+(paket|kleinpaket|warenpost)/.test(p) && !/zuschlag|service/.test(p)) return 'porto';
  if (/service/.test(p)) return 'service';
  return 'sonstiges';
}

export function gewichtsklasseAusProdukt(produkt: string): string | null {
  if (kategorieFuerProdukt(produkt) !== 'porto') return null;
  if (/kleinpaket/i.test(produkt)) return 'Kleinpaket';
  if (/warenpost/i.test(produkt)) return 'Warenpost';
  const m = /(\d+(?:,\d+)?)\s*kg\b/i.exec(produkt);
  return m ? `${m[1]} kg` : 'unbekannt';
}

/** DHL-Gewichtsklassen Paket national (Geschäftskunden): bis 1, 2, 5, 10, 20, 31,5 kg. */
export const DHL_GEWICHTSKLASSEN: ReadonlyArray<readonly [number, string]> = [
  [1000, '1 kg'], [2000, '2 kg'], [5000, '5 kg'], [10_000, '10 kg'], [20_000, '20 kg'], [31_500, '31,5 kg'],
];

/** Klasse einer StoreOS-Sendung aus Gewicht (g) und Versandprodukt (contracts/carrier `ParcelProduct`). */
export function gewichtsklasseFuerSendung(gewichtG: number | null, versandprodukt: string | null): string {
  if (versandprodukt === 'small_parcel') return 'Kleinpaket';
  if (gewichtG === null || gewichtG <= 0) return 'unbekannt';
  for (const [max, k] of DHL_GEWICHTSKLASSEN) if (gewichtG <= max) return k;
  return 'über 31,5 kg';
}

// ── Abgleich je Einlieferungstag (R97) ────────────────────────────────────────

export const ABGLEICH_ERGEBNISSE = ['passt', 'mehr_bei_dhl', 'weniger_bei_dhl', 'andere_gewichtsklasse', 'keine_storeos_sendungen'] as const;
export type AbgleichErgebnis = (typeof ABGLEICH_ERGEBNISSE)[number];
export const ABGLEICH_TEXT: Record<AbgleichErgebnis, string> = {
  passt: 'passt', mehr_bei_dhl: 'mehr bei DHL', weniger_bei_dhl: 'weniger bei DHL', andere_gewichtsklasse: 'andere Gewichtsklasse',
  keine_storeos_sendungen: 'keine StoreOS-Sendungen',
};

export interface AbgleichTag {
  datum: string;
  /** Pakete je Gewichtsklasse laut DHL (Porto-Positionen). */
  dhl: Record<string, number>;
  /** Übergebene StoreOS-Sendungen je Klasse (Tagesabschluss, DHL-Labels). */
  storeos: Record<string, number>;
  dhl_anzahl: number;
  storeos_anzahl: number;
  ergebnis: AbgleichErgebnis;
  text: string;
}

const anzahl = (m: Record<string, number>) => Object.values(m).reduce((s, n) => s + n, 0);

/**
 * Vergleicht einen Tag. `storeosAktiv` = StoreOS hatte im Zeitraum überhaupt eigene DHL-Labels; sonst (Parallelbetrieb,
 * vor der Umschaltung) gibt es nichts zu vergleichen und das Ergebnis ist ein Hinweis, kein Fehler.
 */
export function gleicheTagAb(dhl: Record<string, number>, storeos: Record<string, number>, storeosAktiv: boolean, datum = ''): AbgleichTag {
  const d = anzahl(dhl);
  const s = anzahl(storeos);
  const basis = { datum, dhl, storeos, dhl_anzahl: d, storeos_anzahl: s };
  if (!storeosAktiv) return { ...basis, ergebnis: 'keine_storeos_sendungen', text: 'Keine StoreOS-Sendungen in diesem Zeitraum (Parallelbetrieb): nur DHL-Seite.' };
  if (d > s) return { ...basis, ergebnis: 'mehr_bei_dhl', text: `${d - s} Paket(e) mehr bei DHL als in StoreOS übergeben` };
  if (d < s) return { ...basis, ergebnis: 'weniger_bei_dhl', text: `${s - d} Paket(e) weniger bei DHL als in StoreOS übergeben` };
  const klassen = new Set([...Object.keys(dhl), ...Object.keys(storeos)]);
  const anders = [...klassen].filter((k) => (dhl[k] ?? 0) !== (storeos[k] ?? 0));
  if (anders.length) {
    return { ...basis, ergebnis: 'andere_gewichtsklasse', text: `Gleiche Anzahl, andere Gewichtsklassen: ${anders.map((k) => `${k} DHL ${dhl[k] ?? 0} / StoreOS ${storeos[k] ?? 0}`).join(', ')}` };
  }
  return { ...basis, ergebnis: 'passt', text: `${d} Paket(e), Gewichtsklassen gleich` };
}

// ── Kennzahlen ────────────────────────────────────────────────────────────────

export interface KostenMonat {
  monat: string;
  rechnungen: number;
  pakete: number;
  porto_cents: Cents;
  zuschlaege_cents: Cents;
  pauschale_cents: Cents;
  /** Services und Unbekanntes; nicht in „je Paket“ enthalten. */
  sonstiges_cents: Cents;
  netto_cents: Cents;
  /** (Porto + Zuschläge + Pauschale) / Pakete, gerundet; null ohne Pakete. */
  je_paket_cents: Cents | null;
  je_paket_ohne_pauschale_cents: Cents | null;
  /** Anteil der Pauschale an Porto + Zuschlägen + Pauschale in Basispunkten (82,74 % = 8274). */
  anteil_pauschale_bp: number | null;
  gewichtsklassen: Record<string, number>;
}

export interface KennzahlenEingabe { leistungszeitraum: string | null; datum: string; positionen: ReadonlyArray<Pick<ErkanntePosition, 'kategorie' | 'anzahl' | 'gesamt_cents' | 'gewichtsklasse'>> }

/** Kosten pro Paket je Leistungsmonat (fehlt der Zeitraum: Monat des Rechnungsdatums). Neueste zuerst. */
export function kennzahlenJeMonat(rechnungen: ReadonlyArray<KennzahlenEingabe>): KostenMonat[] {
  const je = new Map<string, KostenMonat>();
  for (const r of rechnungen) {
    const monat = r.leistungszeitraum ?? r.datum.slice(0, 7);
    const k = je.get(monat) ?? {
      monat, rechnungen: 0, pakete: 0, porto_cents: 0, zuschlaege_cents: 0, pauschale_cents: 0, sonstiges_cents: 0, netto_cents: 0,
      je_paket_cents: null, je_paket_ohne_pauschale_cents: null, anteil_pauschale_bp: null, gewichtsklassen: {},
    };
    k.rechnungen++;
    for (const p of r.positionen) {
      k.netto_cents += p.gesamt_cents;
      if (p.kategorie === 'porto') {
        k.porto_cents += p.gesamt_cents;
        k.pakete += p.anzahl;
        const kl = p.gewichtsklasse ?? 'unbekannt';
        k.gewichtsklassen[kl] = (k.gewichtsklassen[kl] ?? 0) + p.anzahl;
      } else if (p.kategorie === 'zuschlag_maut' || p.kategorie === 'zuschlag_energie') k.zuschlaege_cents += p.gesamt_cents;
      else if (p.kategorie === 'pauschale') k.pauschale_cents += p.gesamt_cents;
      else k.sonstiges_cents += p.gesamt_cents;
    }
    je.set(monat, k);
  }
  return [...je.values()].map((k) => {
    const voll = k.porto_cents + k.zuschlaege_cents + k.pauschale_cents;
    return {
      ...k,
      je_paket_cents: k.pakete ? rundeCents(voll / k.pakete) : null,
      je_paket_ohne_pauschale_cents: k.pakete ? rundeCents((k.porto_cents + k.zuschlaege_cents) / k.pakete) : null,
      anteil_pauschale_bp: voll ? Math.round((k.pauschale_cents * 10_000) / voll) : null,
    };
  }).sort((a, b) => b.monat.localeCompare(a.monat));
}

// ── API-Sichten ───────────────────────────────────────────────────────────────

export interface EingangsrechnungZeile {
  id: string;
  lieferant: EingangLieferant;
  lieferant_name: string;
  rechnungsnummer: string;
  datum: string;
  leistungszeitraum: string | null;
  kundennummer: string | null;
  netto_cents: Cents;
  ust_cents: Cents;
  brutto_cents: Cents;
  quelle: EingangQuelle;
  status: EingangStatus;
  klaerfall_grund: string | null;
  pakete: number;
  dateiname: string | null;
  hochgeladen_at: string;
  geprueft_at: string | null;
  festgeschrieben_at: string | null;
  an_buchhaltung_at: string | null;
}

export interface EingangsrechnungDetail extends EingangsrechnungZeile {
  abrechnungsnummern: string[];
  einlieferung_von: string | null;
  einlieferung_bis: string | null;
  steuer: ErkannteSteuer[];
  steuersatz_bp: number | null;
  pdf_sha256: string;
  pdf_bytes: number;
  parser: string;
  parser_version: string;
  pruefung: Pruefergebnis;
  zusammenfassung: ErkannteZusammenfassung[];
  positionen: Array<ErkanntePosition & { nr: number }>;
  pruef_grund: string | null;
  geprueft_von: string | null;
  festgeschrieben_von: string | null;
  verworfen_grund: string | null;
  bhb_receipt_id: string | null;
  outbox: Array<{ id: string; status: string; versuche: number; letzter_fehler: string | null; erledigt_at: string | null }>;
  exporte: Array<{ id: string; adapter: string; modus: string; status: string; request: unknown; response: unknown; created_at: string }>;
}

export interface EingangsAbgleich {
  eingangsrechnung_id: string;
  storeos_aktiv: boolean;
  hinweis: string | null;
  tage: AbgleichTag[];
}
